/**
 * Selects legacy {@link VoiceConversationEngine} vs {@link GeminiLiveVoiceConversationEngine}
 * vs {@link OpenAIRealtimeVoiceConversationEngine}.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Box, ToggleButtonGroup, ToggleButton, Typography, Chip } from "@mui/material";

import VoiceConversationEngine from "./VoiceConversationEngine";
import GeminiLiveVoiceConversationEngine from "./GeminiLiveVoiceConversationEngine";
import OpenAIRealtimeVoiceConversationEngine from "./OpenAIRealtimeVoiceConversationEngine";
import { voiceToggleButtonSx } from "./VoiceProviderToggle";
import {
  resolveVoiceBackend,
  setStoredVoiceBackend,
  VOICE_BACKEND_STORAGE_DEFAULT_KEY,
} from "./voiceConversationFlags";

/**
 * @param {Object} props
 * @param {'legacy'|'gemini_live'|'openai_realtime'|undefined} [props.voiceBackend] - Controlled preference when set.
 * @param {'legacy'|'gemini_live'|'openai_realtime'} [props.defaultVoiceBackend='legacy'] - When prop unset and localStorage empty.
 * @param {string} [props.voiceBackendStorageKey] - localStorage key for persisting backend choice (SC3 uses its own key).
 * @param {boolean} [props.showVoiceBackendToggle=true]
 * @param {function} [props.onFallbackActivated] - Called once when Gemini Live falls back to Classic.
 * @param {Object} props.rest - Forwarded to the active engine.
 */
function VoiceConversationBackendSwitch({
  voiceBackend: voiceBackendProp,
  defaultVoiceBackend = "legacy",
  voiceBackendStorageKey = VOICE_BACKEND_STORAGE_DEFAULT_KEY,
  showVoiceBackendToggle = true,
  onFallbackActivated,
  ...rest
}) {
  const { onEngineError: parentOnEngineError, ...engineRest } = rest;
  const [backend, setBackend] = useState(() =>
    resolveVoiceBackend(voiceBackendProp, defaultVoiceBackend, voiceBackendStorageKey)
  );
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (
      voiceBackendProp === "gemini_live" ||
      voiceBackendProp === "legacy" ||
      voiceBackendProp === "openai_realtime"
    ) {
      setBackend(voiceBackendProp);
    }
  }, [voiceBackendProp]);

  const handleToggle = (_e, value) => {
    if (!value) return;
    setStoredVoiceBackend(value, voiceBackendStorageKey);
    setBackend(value);
  };

  const handleGeminiError = useCallback(
    (error) => {
      parentOnEngineError?.(error);
      if (backend === "gemini_live" && !isFallback) {
        console.warn("[VoiceConversation] Gemini Live error — switching to Classic:", error);
        setBackend("legacy");
        setIsFallback(true);
        onFallbackActivated?.();
      }
    },
    [backend, isFallback, parentOnEngineError, onFallbackActivated]
  );

  const ActiveEngine =
    backend === "gemini_live"
      ? GeminiLiveVoiceConversationEngine
      : backend === "openai_realtime"
        ? OpenAIRealtimeVoiceConversationEngine
        : VoiceConversationEngine;

  const backendLabel = isFallback
    ? "Backup mode"
    : backend === "gemini_live"
      ? "Gemini Live"
      : backend === "openai_realtime"
        ? "OpenAI Realtime"
        : "Voice Active";

  const backendChipColor = isFallback ? "warning" : "default";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Chip
          label={backendLabel}
          color={backendChipColor}
          size="small"
          variant="outlined"
        />
      </Box>
      {showVoiceBackendToggle && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Voice backend
          </Typography>
          <ToggleButtonGroup value={backend} exclusive onChange={handleToggle} size="small" color="primary">
            <ToggleButton value="legacy" sx={voiceToggleButtonSx}>
              Classic pipeline
            </ToggleButton>
            <ToggleButton value="gemini_live" sx={voiceToggleButtonSx}>
              Gemini Live (beta)
            </ToggleButton>
            <ToggleButton value="openai_realtime" sx={voiceToggleButtonSx}>
              OpenAI Realtime
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <ActiveEngine
        {...engineRest}
        onEngineError={backend === "gemini_live" ? handleGeminiError : parentOnEngineError}
        showBackendChip={backend === "gemini_live" ? false : engineRest.showBackendChip}
      />
    </Box>
  );
}

export default VoiceConversationBackendSwitch;
